import { RequestForQuoteStatus } from '~/graphql/graphql';
import type {
  BuyListQuoteComparisonQuery,
  GetRequestForQuoteWithQuotesQuery,
} from '~/graphql/graphql';
import {
  isClosedRequestForQuoteStatus,
  partitionArchivedQuotes,
} from '~/utils/requestForQuoteClosed';
import { buildProductGroups } from '~/pages/rfq-view/itemGroups';
import { showsSplitAwardGrid } from '~/pages/rfq-view/sections/quoteDisplay';
import { comparedTallyOf } from '~/pages/rfq-view/sections/quote-comparison/comparedTally';
import type { QuoteComparison } from '~/pages/rfq-view/sections/quote-comparison/QuoteComparisonSection';

export type ComparableRequestForQuote =
  BuyListQuoteComparisonQuery['buyListRequestForQuotes']['content'][number];

type AggregatedRequestForQuote = NonNullable<
  GetRequestForQuoteWithQuotesQuery['requestForQuote']
>;

type AggregatedQuote =
  GetRequestForQuoteWithQuotesQuery['quotesForRequestForQuote'][number];

export type AggregatedRequest = QuoteComparison & {
  requestsForQuote: ComparableRequestForQuote[];
};

function unionOfRequestedItems(
  requestsForQuote: ComparableRequestForQuote[]
): AggregatedRequestForQuote['items'] {
  const itemByPlatformProductsLengthId = new Map<
    string,
    AggregatedRequestForQuote['items'][number]
  >();

  for (const requestForQuote of requestsForQuote) {
    for (const item of requestForQuote.items) {
      const platformProductsLengthId = item.platformProductsLength.id;
      const alreadyRequested = itemByPlatformProductsLengthId.get(
        platformProductsLengthId
      );
      itemByPlatformProductsLengthId.set(
        platformProductsLengthId,
        alreadyRequested
          ? {
              ...alreadyRequested,
              quantityInPieces:
                alreadyRequested.quantityInPieces + item.quantityInPieces,
            }
          : item
      );
    }
  }

  return [...itemByPlatformProductsLengthId.values()];
}

function aggregateStatus(
  requestsForQuote: ComparableRequestForQuote[]
): RequestForQuoteStatus {
  const stillOpen = requestsForQuote.find(
    (requestForQuote) => !isClosedRequestForQuoteStatus(requestForQuote.status)
  );
  if (stillOpen) {
    return stillOpen.status;
  }
  const everyRequestArchived = requestsForQuote.every(
    (requestForQuote) =>
      requestForQuote.status === RequestForQuoteStatus.Archived
  );
  return everyRequestArchived
    ? RequestForQuoteStatus.Archived
    : RequestForQuoteStatus.Fulfilled;
}

function agreedValue<Value>(
  requestsForQuote: ComparableRequestForQuote[],
  readValue: (requestForQuote: ComparableRequestForQuote) => Value,
  identify: (value: Value) => unknown = (value) => value
): Value | null {
  const [firstRequestForQuote, ...otherRequestsForQuote] = requestsForQuote;
  const firstValue = readValue(firstRequestForQuote);
  const everyRequestAgrees = otherRequestsForQuote.every(
    (requestForQuote) =>
      identify(readValue(requestForQuote)) === identify(firstValue)
  );
  return everyRequestAgrees ? firstValue : null;
}

function identifyById(value: { id: string } | null | undefined): string | null {
  return value?.id ?? null;
}

function identifyMoney(
  money: { amount: number; currency: string } | null | undefined
): string | null {
  return money ? `${money.amount} ${money.currency}` : null;
}

function quotesWithTheirRequest(
  requestsForQuote: ComparableRequestForQuote[]
): AggregatedQuote[] {
  return requestsForQuote.flatMap((requestForQuote) =>
    requestForQuote.quotes.map((quote) => ({
      ...quote,
      requestForQuote: {
        id: requestForQuote.id,
        status: requestForQuote.status,
      },
    }))
  );
}

function pricingModeOf(useTallyPrice: boolean): 'tally' | 'per-item' {
  return useTallyPrice ? 'tally' : 'per-item';
}

function comparisonKeyOf(requestForQuote: ComparableRequestForQuote): string {
  return requestForQuote.tallyPlatformProduct
    ? `tally-comparison-${requestForQuote.id}`
    : `pricing-mode-${pricingModeOf(requestForQuote.useTallyPrice)}`;
}

function aggregateOneComparison(
  requestsForQuote: ComparableRequestForQuote[],
  buyListId: string,
  key: string
): AggregatedRequest {
  const [firstRequestForQuote] = requestsForQuote;
  const { activeQuotes, archivedQuotes } = partitionArchivedQuotes(
    quotesWithTheirRequest(requestsForQuote)
  );
  const items = unionOfRequestedItems(requestsForQuote);

  return {
    key,
    tally: comparedTallyOf(firstRequestForQuote),
    requestForQuote: {
      id: buyListId,
      status: aggregateStatus(requestsForQuote),
      createdAt: firstRequestForQuote.createdAt,
      useTallyPrice: firstRequestForQuote.useTallyPrice,
      leadTimeDays: agreedValue(
        requestsForQuote,
        (requestForQuote) => requestForQuote.leadTimeDays
      ),
      currency: agreedValue(
        requestsForQuote,
        (requestForQuote) => requestForQuote.currency
      ),
      tallyPlatformProduct: agreedValue(
        requestsForQuote,
        (requestForQuote) => requestForQuote.tallyPlatformProduct,
        identifyById
      ),
      cannedTally: agreedValue(
        requestsForQuote,
        (requestForQuote) => requestForQuote.cannedTally,
        identifyById
      ),
      cannedTallyCount: agreedValue(
        requestsForQuote,
        (requestForQuote) => requestForQuote.cannedTallyCount
      ),
      shippingLocation: agreedValue(
        requestsForQuote,
        (requestForQuote) => requestForQuote.shippingLocation,
        identifyById
      ),
      pickupPriceForTally: agreedValue(
        requestsForQuote,
        (requestForQuote) => requestForQuote.pickupPriceForTally,
        identifyMoney
      ),
      deliveryPriceForTally: agreedValue(
        requestsForQuote,
        (requestForQuote) => requestForQuote.deliveryPriceForTally,
        identifyMoney
      ),
      items,
    },
    quotes: activeQuotes,
    archivedQuotes,
    groups: buildProductGroups(
      items,
      activeQuotes.flatMap((quote) => quote.items)
    ),
    requestsForQuote,
  };
}

export function aggregateRequestsForQuote(
  requestsForQuote: ComparableRequestForQuote[],
  buyListId: string
): AggregatedRequest[] {
  const requestsByComparisonKey = new Map<
    string,
    ComparableRequestForQuote[]
  >();
  for (const requestForQuote of requestsForQuote) {
    const key = comparisonKeyOf(requestForQuote);
    requestsByComparisonKey.set(key, [
      ...(requestsByComparisonKey.get(key) ?? []),
      requestForQuote,
    ]);
  }

  return [...requestsByComparisonKey.entries()].map(([key, comparedRequests]) =>
    aggregateOneComparison(comparedRequests, buyListId, key)
  );
}

export function findPlatformProductLengthIdsInSellerQuotes(
  comparisons: readonly AggregatedRequest[]
): ReadonlySet<string> {
  return new Set(
    comparisons
      .filter(({ requestForQuote, quotes }) =>
        showsSplitAwardGrid(requestForQuote, quotes)
      )
      .flatMap(({ groups }) => groups.flatMap(({ rows }) => rows))
      .filter(
        ({ requestedQuantityInPieces }) => requestedQuantityInPieces != null
      )
      .map(({ platformProductLengthId }) => platformProductLengthId)
  );
}
